import React from 'react'
import { Link, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { FormattedMessage } from 'react-intl'
import ReplacementProductsCarousel from '@src/components/ReplacementProductsCarousel'
import ResponsiveModal, {
  ResponsiveModalBody,
  ModalSizes,
} from '@src/components/ResponsiveModal/ResponsiveModal'
import { getSellerName } from '@src/utils/searchUtils'
import { DiscontinuedMaterialPricingDetailFragment } from '@src/fragments/ProductPricing.generated'
import { PdpFieldsFragment } from '@src/queries/PDPQuery.generated'
import { ContactSellerReasonType, sellerRoutes, useRouter } from '@src/routes'
import { Linq } from '@src/components/Linq'
import messages from '@utils/MessageSets/defineReplacementProductsCarouselModalMessages'
import { conformReplacementProductToCard } from '../ReplacementProductsCarousel/replacementProductToProductCard'
import { CarouselProductTypes } from '../DynamicProductCarousel/DynamicProductCardTypes'

const useStyles = makeStyles()((theme) => ({
  link: {
    cursor: 'pointer',
    margin: theme.spacing(0, 1),
  },
  message: {
    marginLeft: theme.spacing(1),
  },
}))

interface ReplacementProductsCarouselModalProps {
  errorKey: string
  product?: Partial<PdpFieldsFragment>
  replacementProducts?: DiscontinuedMaterialPricingDetailFragment['replacementProducts']
  onClose: () => void
}

const ReplacementProductsCarouselModal: React.FC<
  ReplacementProductsCarouselModalProps
> = ({ errorKey, product, replacementProducts, onClose }) => {
  const { classes } = useStyles()
  const router = useRouter()
  const sellerName =
    product?.displaySellerName || getSellerName(product?.attributes)
  const mainProductToCompare = {
    productKey: product?.productKey,
    brandKey: product?.brand?.key,
    images: product?.images,
    showViewDetailsLink: true,
  }

  // Replacement products slightly differ from normal carousel products, but data structure needs to match for analytics reporting.
  // Function conforms data differences to match analytics needs.
  const conformedReplacementProducts =
    conformReplacementProductToCard(replacementProducts)

  const getAdditionalMessage = () => {
    if (product?.isMarketplace) {
      return (
        <Linq
          {...sellerRoutes.contact(
            sellerName,
            product?.marketplaceSellerId ?? '',
            router.asPath,
            ContactSellerReasonType.OFFER_MESSAGING,
            product?.marketplaceOfferId ?? ''
          )}
        >
          <FormattedMessage
            {...messages['CONTACT_SELLER_WITH_NAME']}
            values={{ sellerName }}
          />
        </Linq>
      )
    }

    if (replacementProducts?.length) {
      return (
        <Typography className={classes.message} variant="inherit">
          <FormattedMessage {...messages['REPLACEMENT_PRODUCT_MESSAGE']} />
        </Typography>
      )
    }

    return (
      <>
        <Link className={classes.link} href={'/support/customer-support'}>
          <FormattedMessage {...messages['CONTACT_TECHNICAL_SERVICE']} />
        </Link>
        <FormattedMessage {...messages['FOR_ASSISTANCE']} />
      </>
    )
  }

  return (
    <ResponsiveModal
      size={ModalSizes.Small}
      open
      onClose={onClose}
      renderTitle={() =>
        messages[errorKey]?.title ? (
          <Typography variant="h2">
            <FormattedMessage
              id={errorKey}
              defaultMessage="{title}"
              values={{
                title: messages[errorKey]?.title,
              }}
            />
          </Typography>
        ) : null
      }
    >
      <ResponsiveModalBody>
        <FormattedMessage
          {...messages[errorKey]}
          values={{
            productNumber: product?.productNumber,
          }}
        />
        <FormattedMessage
          id={errorKey}
          defaultMessage="{additionalMessage}"
          values={{
            additionalMessage: getAdditionalMessage(),
          }}
        />
        {conformedReplacementProducts.length ? (
          <ReplacementProductsCarousel
            type={CarouselProductTypes.RecommendedProducts}
            preloadCardImages
            mainProduct={mainProductToCompare}
            products={conformedReplacementProducts}
            slidesToShow={1}
            withoutControls={conformedReplacementProducts?.length <= 4}
          />
        ) : null}
      </ResponsiveModalBody>
    </ResponsiveModal>
  )
}

export default ReplacementProductsCarouselModal
